/**
 * Google reCAPTCHA（score 方式。Google Cloud 発行の鍵のため enterprise.js）のトークン取得。
 *
 * ログイン・パスワード再設定の開始・確定の 3 リクエストに `recaptchaToken` として載せ、
 * api が Google で検証する（ADR-0062）。パズルもチェックボックスも出ない。
 *
 * - トークンは送信の直前に取り、そのリクエストにだけ使う。Google 側で 2 分で失効し
 *   1 回しか使えないため、取り置くと api が `timeout-or-duplicate` で不合格にする
 * - 取得に失敗したら（鍵なし・スクリプト未読込・ネットワーク・localhost）トークン無しで送る。
 *   api は当面欠落を受理する。ローカル（鍵のドメインリスト外）もこの経路で動く
 * - Storybook / VRT では鍵を空にしてスクリプトを読まない（ネットワークへ出さない）
 */

/** api が照合する action 名。3 画面とも同じ値にする（画面ごとに変えない）。 */
export const RECAPTCHA_ACTION = 'login'

/** トークン取得を待つ上限。これを超えたらトークン無しで送信する。 */
export const RECAPTCHA_TIMEOUT_MS = 5000

const SCRIPT_ID = 'google-recaptcha-enterprise'
const BADGE_STYLE_ID = 'google-recaptcha-badge-hidden'

interface GrecaptchaEnterprise {
  ready: (callback: () => void) => void
  execute: (siteKey: string, options: { action: string }) => Promise<string>
}

declare global {
  interface Window {
    grecaptcha?: { enterprise?: GrecaptchaEnterprise }
  }
}

const siteKey = (): string => process.env.GOOGLE_RECAPTCHA_SITE_KEY ?? ''

/** スクリプトの読み込み。同じページで 1 回だけ行い、以降は同じ Promise を返す。 */
let loading: Promise<void> | null = null

/**
 * バッジを隠す。Google の規約上、隠す場合は reCAPTCHA の注記（RecaptchaNotice）を
 * 画面に出す必要がある。ログイン後の画面にもバッジが残らないよう、画面単位ではなく
 * document 全体へ当てる。
 */
const hideBadge = (): void => {
  if (document.getElementById(BADGE_STYLE_ID) != null) return
  const style = document.createElement('style')
  style.id = BADGE_STYLE_ID
  style.textContent = '.grecaptcha-badge { visibility: hidden; }'
  document.head.appendChild(style)
}

/** reCAPTCHA のスクリプトを読み込む（鍵があることは呼び出し側で確認済み）。 */
const loadRecaptcha = (key: string): Promise<void> => {
  if (loading != null) return loading

  hideBadge()
  loading = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script')
    script.id = SCRIPT_ID
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${encodeURIComponent(key)}&hl=ja`
    script.async = true
    script.onload = () => { resolve() }
    script.onerror = () => {
      // 失敗した読み込みは捨て、次の送信で読み直せるようにする。
      loading = null
      script.remove()
      reject(new Error('reCAPTCHA のスクリプトを読み込めませんでした'))
    }
    document.head.appendChild(script)
  })
  return loading
}

/**
 * 画面表示時にスクリプトを読み込んでおく。鍵が無ければ何もしない。
 * 送信時のトークン取得を待たずに済み、score の判定材料（ページ上の操作）も集まる。
 * 読み込みに失敗しても送信時にもう一度試すので、ここでは握りつぶす。
 */
export const preloadRecaptcha = (): void => {
  const key = siteKey()
  if (key === '') return
  loadRecaptcha(key).catch(() => {})
}

const execute = async (key: string): Promise<string> => {
  await loadRecaptcha(key)
  const enterprise = window.grecaptcha?.enterprise
  if (enterprise == null) {
    throw new Error('reCAPTCHA が初期化されていません')
  }
  return await new Promise<string>((resolve, reject) => {
    enterprise.ready(() => {
      enterprise.execute(key, { action: RECAPTCHA_ACTION }).then(resolve, reject)
    })
  })
}

/**
 * 送信直前に reCAPTCHA トークンを取得する。取れなければ undefined を返し、例外は投げない。
 * 呼び出し側はトークン無しでそのまま送信する。
 */
export const getRecaptchaToken = async (): Promise<string | undefined> => {
  const key = siteKey()
  if (key === '') return undefined

  let timer: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<undefined>((resolve) => {
    timer = setTimeout(() => { resolve(undefined) }, RECAPTCHA_TIMEOUT_MS)
  })

  try {
    const token = await Promise.race([execute(key), timeout])
    return token === '' ? undefined : token
  } catch (error) {
    console.warn('reCAPTCHA トークンを取得できませんでした。トークン無しで送信します:', error)
    return undefined
  } finally {
    clearTimeout(timer)
  }
}
